import { Link } from "react-router-dom";
import { ArrowRight, Sofa, UtensilsCrossed, Sparkles, ShieldCheck } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const CATEGORIES = [
  {
    icon: Sofa,
    title: "Furniture",
    testid: "accept-furniture",
    items: [
      "Couches and loveseats", "Living room chairs", "Accent chairs", "Recliners",
      "Dining tables", "Dining chairs", "Bed frames", "Mattresses in clean, acceptable condition",
      "Dressers", "Nightstands", "Coffee tables", "End tables", "Desks", "Office chairs",
      "Bookshelves", "Storage furniture", "Children's furniture", "Lamps",
    ],
  },
  {
    icon: UtensilsCrossed,
    title: "Household Essentials",
    testid: "accept-essentials",
    items: [
      "Air fryers", "Toasters", "Microwaves", "Blenders", "Dishes and dinnerware",
      "Pots and pans", "Cooking utensils", "Silverware", "Cups and glasses",
      "Bedding", "Sheets", "Blankets", "Comforters", "Towels", "Linens", "Pillows",
      "Shower caddies", "Shower curtains", "Laundry baskets", "Storage containers",
    ],
  },
  {
    icon: Sparkles,
    title: "Home Décor",
    testid: "accept-decor",
    items: [
      "Rugs", "Curtains", "Wall décor", "Mirrors", "Picture frames", "Vases",
      "Decorative items", "Plants and planters", "Other clean, usable home items",
    ],
  },
];

export default function WhatWeAccept() {
  return (
    <div data-testid="what-we-accept-page">
      <PageHero overline="Donation Guidelines" title="WHAT WE" italic="gratefully accept." testid="what-we-accept-headline">
        Gently used, clean, and safe items help families furnish their homes with dignity. If you'd use it in your
        own home, it can change someone else's.
      </PageHero>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="grid gap-8 lg:grid-cols-3">
          {CATEGORIES.map((c, i) => (
            <FadeUp key={c.title} delay={i * 0.12}>
              <div data-testid={c.testid} className="bento-card h-full bg-white/60">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-olive">
                    <c.icon className="h-6 w-6 text-cream" />
                  </div>
                  <h2 className="font-serif text-2xl text-olive">{c.title}</h2>
                </div>
                <ul className="mt-6 grid grid-cols-1 gap-x-4 gap-y-2.5">
                  {c.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-moss">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-burgundy" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.2}>
          <div data-testid="dignity-note" className="mt-12 flex flex-col items-start gap-4 rounded-2xl border border-burgundy/25 bg-beige p-8 sm:flex-row sm:items-center">
            <ShieldCheck className="h-10 w-10 shrink-0 text-burgundy" />
            <div>
              <h3 className="font-serif text-xl text-olive">Our dignity standard</h3>
              <p className="mt-2 text-sm leading-relaxed text-moss">
                Fresh Start Furnishings may accept or decline donations based on condition, safety, current needs,
                storage space, and transportation availability. Every item we pass on should feel like a gift, not a hand-me-down.
              </p>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.3} className="mt-12 text-center">
          <Link to="/donate-items" data-testid="start-donation-button" className="btn-accent !px-10 !py-4 text-base">
            Start a Donation <ArrowRight className="h-5 w-5" />
          </Link>
        </FadeUp>
      </section>

      <Marquee dark={false} />
    </div>
  );
}
